/**
 * Tags Popup
 * Handles mobile popup for category filters
 *
 * - data-enable-mobile-scroll="true" - Enable #filters scroll on mobile (default: false)
 *
 * @class
 */
class TagsPopup {

	/**
	 * Creates an instance of TagsPopup
	 * @param {HTMLElement} block - The tags popup block element
	 */
	constructor( block ) {

		this.block = block;
		this.popup = null;
		this.mobileButton = null;
		this.closeButton = null;
		this.termsContainers = null;
		this.previouslyFocusedElement = null;
		this.focusableElements = null;
		this.scrollPosition = 0;

		this.enablePopupOnMobile = block.dataset.enablePopup === 'true';
		this.hasAllButton = block.dataset.hasAllButton === 'true';
		this.allButtonText = block.dataset.allButtonText || 'All';
		this.allButtonUrl = block.dataset.allButtonUrl || '';
		this.allButtonOpensInNewTab = block.dataset.allButtonNewTab === 'true';
		this.enableMobileScroll = block.dataset.enableMobileScroll === 'true';

		// Find parent .b-posts-grid for proper scroll target
		this.scrollTarget = this.block.closest( '.b-posts-grid' ) || this.block;

		// Ensure scroll target has ID for hash navigation
		if ( !this.scrollTarget.id ) {

			this.scrollTarget.id = 'filters';

		}

		this.ensureCleanState();

		if ( this.enablePopupOnMobile || this.hasAllButton ) {

			this.init();

		}

	}

	/**
	 * Ensures clean state on initialization
	 * @return {void}
	 */
	ensureCleanState() {

		document.body.classList.remove( 'has-tags-popup-open' );
		document.body.style.removeProperty( '--scroll-position' );

	}

	/**
	 * Initializes the popup functionality
	 * @return {void}
	 */
	init() {

		this.termsContainers = Array.from(
			this.block.querySelectorAll( '.wp-block-term-template' ),
		);

		if ( !this.termsContainers || this.termsContainers.length === 0 ) {

			return;

		}

		this.injectAllButton( this.termsContainers[0] );

		// Handle hash-based auto-scroll (always, regardless of popup state)
		this.handleHashOnLoad();

		if ( this.enablePopupOnMobile ) {

			this.mobileButton = this.block.querySelector(
				'.b-tags-popup__mobile-button',
			);

			if ( !this.mobileButton ) {

				return;

			}

			this.createPopup();
			this.markActiveTerm();
			this.attachMobileButtonListener();
			this.attachPopupListeners();
			this.attachGlobalListeners();

		} else {

			this.markActiveTerm();

		}

		// Attach term link handler for desktop view (always)
		this.block.addEventListener( 'click', this.handleTermLinkClick );

	}

	/**
	 * Injects ALL button as first term in the list
	 * @param {HTMLElement} container - The terms container (ul element)
	 * @return {void}
	 */
	injectAllButton( container ) {

		if ( !this.hasAllButton || !container || !this.allButtonUrl ) {

			return;

		}

		const existingAllButton = container.querySelector(
			'.wp-block-term--all-button',
		);

		if ( existingAllButton ) {

			return;

		}

		const allButtonItem = document.createElement( 'li' );
		allButtonItem.className = 'wp-block-term wp-block-term--all-button';

		const allButtonWrapper = document.createElement( 'p' );
		allButtonWrapper.className = 'wp-block-term-name';

		const allButtonLink = document.createElement( 'a' );
		allButtonLink.href = this.allButtonUrl;
		allButtonLink.textContent = this.allButtonText;

		if ( this.allButtonOpensInNewTab ) {

			allButtonLink.target = '_blank';
			allButtonLink.rel = 'noopener noreferrer';

		}

		allButtonWrapper.appendChild( allButtonLink );
		allButtonItem.appendChild( allButtonWrapper );

		container.insertBefore( allButtonItem, container.firstChild );

	}

	/**
	 * Marks the active term based on current URL
	 * @return {void}
	 */
	markActiveTerm() {

		const currentURL = window.location.href;

		this.termsContainers.forEach( ( container ) => {

			this.markActiveTermInContainer( container, currentURL );

		} );

		if ( this.popup ) {

			const popupTermsContainer = this.popup.querySelector(
				'.b-tags-popup__terms',
			);

			if ( popupTermsContainer ) {

				this.markActiveTermInContainer( popupTermsContainer, currentURL );

			}

		}

	}

	/**
	 * Marks active term in a specific container
	 * @param {HTMLElement} container - Container with terms
	 * @param {string} currentURL - Current page URL
	 * @return {void}
	 */
	markActiveTermInContainer( container, currentURL ) {

		if ( !container ) {

			return;

		}

		const termLinks = container.querySelectorAll( '.wp-block-term-name a' );

		termLinks.forEach( ( link ) => {

			const termItem = link.closest( '.wp-block-term' );

			if ( !termItem ) {

				return;

			}

			// Normalize URLs: remove hash first, then trailing slash
			const linkURL = link.href.split( '#' )[0].replace( /\/$/, '' );
			const normalizedCurrentURL = currentURL
				.split( '#' )[0]
				.replace( /\/$/, '' );

			if ( linkURL === normalizedCurrentURL ) {

				termItem.classList.add( 'is-active' );
				link.setAttribute( 'aria-current', 'page' );

			} else {

				termItem.classList.remove( 'is-active' );
				link.removeAttribute( 'aria-current' );

			}

		} );

	}

	/**
	 * Creates popup overlay and content
	 * @return {void}
	 */
	createPopup() {

		this.popup = document.createElement( 'div' );
		this.popup.className = 'b-tags-popup__overlay';
		this.popup.setAttribute( 'aria-hidden', 'true' );
		this.popup.setAttribute( 'role', 'dialog' );
		this.popup.setAttribute( 'aria-modal', 'true' );
		this.popup.setAttribute( 'aria-label', 'Category filters' );

		const popupContent = document.createElement( 'div' );
		popupContent.className = 'b-tags-popup__content';

		this.closeButton = document.createElement( 'button' );
		this.closeButton.className = 'b-tags-popup__close';
		this.closeButton.setAttribute( 'aria-label', 'Close filters' );
		this.closeButton.setAttribute( 'type', 'button' );

		const closeIcon = document.createElement( 'span' );
		closeIcon.setAttribute( 'aria-hidden', 'true' );
		closeIcon.textContent = '×';
		this.closeButton.appendChild( closeIcon );

		const mergedTermsList = document.createElement( 'ul' );
		mergedTermsList.className = 'b-tags-popup__terms';

		this.termsContainers.forEach( ( container ) => {

			const termItems = container.querySelectorAll( 'li' );
			termItems.forEach( ( item ) => {

				const itemClone = item.cloneNode( true );
				mergedTermsList.appendChild( itemClone );

			} );

		} );

		this.markActiveTermInContainer( mergedTermsList, window.location.href );

		popupContent.appendChild( this.closeButton );
		popupContent.appendChild( mergedTermsList );
		this.popup.appendChild( popupContent );

		document.body.appendChild( this.popup );

	}

	/**
	 * Attaches mobile button listener (called once)
	 * @return {void}
	 */
	attachMobileButtonListener() {

		this.mobileButton?.addEventListener( 'click', ( e ) => {

			e.stopPropagation();
			this.openPopup();

		} );

	}

	/**
	 * Attaches popup-specific listeners (can be called multiple times)
	 * @return {void}
	 */
	attachPopupListeners() {

		this.closeButton?.addEventListener( 'click', () => this.closePopup() );
		this.popup?.addEventListener( 'click', ( e ) =>
			this.handleOverlayClick( e ),
		);
		this.popup?.addEventListener( 'click', ( e ) =>
			this.handleTermLinkClick( e ),
		);
		this.popup?.addEventListener( 'keydown', ( e ) => this.handleFocusTrap( e ) );

	}

	/**
	 * Attaches global listeners (called once)
	 * @return {void}
	 */
	attachGlobalListeners() {

		document.addEventListener( 'keydown', ( e ) => this.handleEscKey( e ) );
		window.addEventListener( 'resize', () => this.handleResize() );

	}

	/**
	 * Opens the popup
	 * @return {void}
	 */
	openPopup() {

		if ( !this.popup ) {

			return;

		}

		this.previouslyFocusedElement = this.popup.ownerDocument.activeElement;

		this.scrollPosition =
			window.pageYOffset || document.documentElement.scrollTop;

		document.body.style.setProperty(
			'--scroll-position',
			this.scrollPosition,
		);
		document.body.classList.add( 'has-tags-popup-open' );

		this.popup.classList.add( 'is-open' );
		this.popup.setAttribute( 'aria-hidden', 'false' );
		this.mobileButton?.setAttribute( 'aria-expanded', 'true' );

		requestAnimationFrame( () => {

			this.closeButton?.focus();
			this.updateFocusableElements();

		} );

	}

	/**
	 * Closes the popup
	 * @return {void}
	 */
	closePopup() {

		if ( !this.popup ) {

			return;

		}

		this.popup.classList.remove( 'is-open' );
		this.popup.setAttribute( 'aria-hidden', 'true' );
		this.mobileButton?.setAttribute( 'aria-expanded', 'false' );

		document.body.classList.remove( 'has-tags-popup-open' );
		document.body.style.removeProperty( '--scroll-position' );

		window.scrollTo( 0, this.scrollPosition );

		if (
			this.previouslyFocusedElement &&
			typeof this.previouslyFocusedElement.focus === 'function'
		) {

			this.previouslyFocusedElement.focus();
			this.previouslyFocusedElement = null;

		}

	}

	/**
	 * Handles window resize
	 * @return {void}
	 */
	handleResize() {

		if (
			this.popup?.classList.contains( 'is-open' ) &&
			window.innerWidth > 568
		) {

			this.closePopup();

		}

	}

	/**
	 * Updates list of focusable elements in popup
	 * @return {void}
	 */
	updateFocusableElements() {

		if ( !this.popup ) {

			return;

		}

		const focusableSelectors =
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
		this.focusableElements = Array.from(
			this.popup.querySelectorAll( focusableSelectors ),
		).filter( ( el ) => !el.hasAttribute( 'disabled' ) );

	}

	/**
	 * Handles focus trap inside popup
	 * @param {KeyboardEvent} e - Keyboard event
	 * @return {void}
	 */
	handleFocusTrap( e ) {

		if (
			e.key !== 'Tab' ||
			!this.focusableElements ||
			this.focusableElements.length === 0
		) {

			return;

		}

		const firstFocusable = this.focusableElements[0];
		const lastFocusable =
			this.focusableElements[this.focusableElements.length - 1];

		if (
			e.shiftKey &&
			this.popup.ownerDocument.activeElement === firstFocusable
		) {

			e.preventDefault();
			lastFocusable.focus();

		} else if (
			!e.shiftKey &&
			this.popup.ownerDocument.activeElement === lastFocusable
		) {

			e.preventDefault();
			firstFocusable.focus();

		}

	}

	/**
	 * Handles clicks on overlay (outside content)
	 * @param {MouseEvent} e - Click event
	 * @return {void}
	 */
	handleOverlayClick( e ) {

		if ( e.target.classList.contains( 'b-tags-popup__overlay' ) ) {

			this.closePopup();

		}

	}

	/**
	 * Handles hash-based auto-scroll on page load
	 * @return {void}
	 */
	handleHashOnLoad = () => {

		if ( window.location.hash === '#filters' ) {

			// Skip header transition to avoid double animation
			this.forceHeaderStickyState();

			// Double RAF ensures DOM is ready
			requestAnimationFrame( () => {

				requestAnimationFrame( () => {

					this.scrollToTarget();

				} );

			} );

		}

	};

	/**
	 * Forces header to sticky state without animation
	 * Prevents double animation (header transition + scroll)
	 * @return {void}
	 */
	forceHeaderStickyState = () => {

		const header = document.querySelector(
			'.p-header, .p-header--sticky-scroll',
		);

		if ( !header ) {

			return;

		}

		const originalTransition = header.style.transition;

		// Temporarily disable transitions
		header.style.transition = 'none';
		header.classList.add( 'p-header--is-scrolled' );

		// Force reflow
		void header.offsetHeight;

		requestAnimationFrame( () => {

			header.style.transition = originalTransition;

		} );

	};

	/**
	 * Scrolls to target element (parent .b-posts-grid) with header offset
	 * @return {void}
	 */
	scrollToTarget = () => {

		const header = document.querySelector(
			'.p-header, .p-header--sticky-scroll',
		);
		const headerHeight = header ? header.offsetHeight : 0;
		const targetTop = this.scrollTarget.getBoundingClientRect().top;

		let scrollPosition =
			window.pageYOffset + targetTop - headerHeight - 20;

		// Ensure scroll position is > 80px to keep header hidden
		// HeaderScrollObserver shows header when scroll <= MIN_SCROLL_TO_HIDE (80px)
		// Admin bar (32px when logged in) affects these calculations
		const MIN_SCROLL_TO_KEEP_HEADER_HIDDEN = 85;

		if ( scrollPosition < MIN_SCROLL_TO_KEEP_HEADER_HIDDEN ) {

			scrollPosition = MIN_SCROLL_TO_KEEP_HEADER_HIDDEN;

		}

		window.scrollTo( {
			top: scrollPosition,
			behavior: 'smooth',
		} );

	};

	/**
	 * Handles clicks on term links - adds hash for auto-scroll on target page
	 * @param {MouseEvent} e - Click event
	 * @return {void}
	 */
	handleTermLinkClick = ( e ) => {

		const link = e.target.closest( '.wp-block-term-name a' );

		if ( !link ) {

			return;

		}

		const isInPopup = link.closest( '.b-tags-popup__overlay.is-open' );

		if ( isInPopup ) {

			this.closePopup();

			// Mobile scroll disabled by default
			if ( !this.enableMobileScroll ) {

				return;

			}

		}

		const href = link.getAttribute( 'href' );

		if ( !href ) {

			return;

		}

		try {

			const url = new URL( href, window.location.href );

			if ( url.origin === window.location.origin ) {

				if ( url.hash !== '#filters' ) {

					e.preventDefault();
					url.hash = 'filters';
					window.location.href = url.toString();

				}

			}

		} catch ( error ) {

			// eslint-disable-next-line no-console
			console.error( 'Failed to parse URL:', error );

		}

	};

	/**
	 * Handles ESC key press
	 * @param {KeyboardEvent} e - Keyboard event
	 * @return {void}
	 */
	handleEscKey( e ) {

		if ( e.key === 'Escape' && this.popup?.classList.contains( 'is-open' ) ) {

			this.closePopup();

		}

	}

	/**
	 * Destroys the instance and cleans up
	 * @return {void}
	 */
	destroy() {

		this.block?.removeEventListener( 'click', this.handleTermLinkClick );

		if ( this.popup && this.popup.parentNode ) {

			this.popup.parentNode.removeChild( this.popup );

		}

		this.popup = null;
		this.closeButton = null;
		this.previouslyFocusedElement = null;
		this.focusableElements = null;

	}

}

const instances = new WeakMap();

document.querySelectorAll( '.b-tags-popup' ).forEach( ( block ) => {

	if ( instances.has( block ) ) {

		return;

	}

	const instance = new TagsPopup( block );
	instances.set( block, instance );

} );

window.addEventListener( 'beforeunload', () => {

	document.body.classList.remove( 'has-tags-popup-open' );
	document.body.style.removeProperty( '--scroll-position' );

	document.querySelectorAll( '.b-tags-popup' ).forEach( ( block ) => {

		const instance = instances.get( block );
		if ( instance && typeof instance.destroy === 'function' ) {

			instance.destroy();

		}

	} );

} );

window.addEventListener( 'pageshow', ( e ) => {

	if ( e.persisted ) {

		document.body.classList.remove( 'has-tags-popup-open' );
		document.body.style.removeProperty( '--scroll-position' );

		document.querySelectorAll( '.b-tags-popup' ).forEach( ( block ) => {

			const instance = instances.get( block );

			if (
				instance &&
				( !instance.popup || !document.body.contains( instance.popup ) )
			) {

				if ( !instance.termsContainers ) {

					instance.termsContainers = Array.from(
						instance.block.querySelectorAll(
							'.wp-block-term-template',
						),
					);

				}

				instance.createPopup();
				instance.attachPopupListeners();

			}

		} );

	}

} );
